import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, API } from "../../lib/api";
import { createRecorder, extForMime, logRecorderError, micErrorMessage } from "../../lib/recorder";
import { Upload, Loader2, FileText, Trash2, ExternalLink, Paperclip, FlaskConical, Sparkles, Camera, Mic, Square, StickyNote, Send, AudioLines } from "lucide-react";
import { toast } from "sonner";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { useAuth } from "../../context/AuthContext";
import { isPremium, startProCheckout } from "../../lib/premium";

const CATEGORIES = [
  { v: "laboratorio", l: "Laboratorio" },
  { v: "estudio", l: "Estudio / Gabinete" },
  { v: "imagen", l: "Imagen / Radiografía" },
  { v: "receta_externa", l: "Receta externa" },
  { v: "foto", l: "Foto clínica" },
  { v: "otro", l: "Otro" },
];
const CAT_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.v, c.l]));

function Thumb({ att }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let active = true;
    api.get(`/attachments/${att.attachment_id}/download`, { responseType: "blob" })
      .then((r) => { if (active) { url = URL.createObjectURL(r.data); setSrc(url); } })
      .catch(() => {});
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [att.attachment_id]);
  if (!src) return <div className="w-full h-32 rounded-xl bg-stone-100 flex items-center justify-center"><Loader2 size={18} className="animate-spin text-muted-foreground" /></div>;
  return <img src={src} alt={att.title} className="w-full h-32 object-cover rounded-xl" data-testid={`att-thumb-${att.attachment_id}`} />;
}

export default function AttachmentsPanel({ patient, onSendToConsult }) {
  const { user } = useAuth();
  const premium = isPremium(user);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [category, setCategory] = useState("laboratorio");
  const [title, setTitle] = useState("");
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const token = localStorage.getItem("session_token");
  const [analyzingId, setAnalyzingId] = useState(null);
  const [result, setResult] = useState(null); // { title, text }
  // Nota de laboratorio (escribir / dictar)
  const [noteTitle, setNoteTitle] = useState("");
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRec = useRef(null);
  const chunks = useRef([]);

  const canAnalyze = (att) => att.is_note || att.is_image || (att.content_type === "application/pdf") || (att.original_filename || "").toLowerCase().endsWith(".pdf");

  const analyze = async (att) => {
    setAnalyzingId(att.attachment_id);
    try {
      const r = await api.post(`/attachments/${att.attachment_id}/analyze`);
      setResult({ title: att.title || att.original_filename, text: r.data.analysis });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo analizar el estudio");
    } finally {
      setAnalyzingId(null);
    }
  };

  const load = () => api.get(`/patients/${patient.patient_id}/attachments`).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, [patient.patient_id]); // eslint-disable-line

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("El archivo supera 15 MB"); return; }
    setUploading(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      fd.append("title", title || file.name);
      await api.post(`/patients/${patient.patient_id}/attachments`, fd, {
        onUploadProgress: (p) => setProgress(Math.round((p.loaded / (p.total || p.loaded)) * 100)),
      });
      toast.success("Adjuntado al expediente");
      setTitle("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al subir el archivo");
    } finally {
      setUploading(false); setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
      if (camRef.current) camRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este elemento del expediente?")) return;
    try { await api.delete(`/attachments/${id}`); setItems((l) => l.filter((x) => x.attachment_id !== id)); toast.success("Eliminado"); }
    catch (e) { toast.error("No se pudo eliminar"); }
  };

  const saveNote = async () => {
    if (!noteText.trim()) { toast.error("Escribe o dicta la nota primero"); return; }
    setSavingNote(true);
    try {
      await api.post(`/patients/${patient.patient_id}/lab-note`, {
        title: noteTitle || "Nota de laboratorio", note: noteText, category: "laboratorio",
      });
      toast.success("Nota de laboratorio guardada");
      setNoteText(""); setNoteTitle("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar la nota"); }
    finally { setSavingNote(false); }
  };

  const startDictation = async () => {
    if (!premium) {
      toast.error("El dictado con ClinPed Voice es del Plan Pro.", { action: { label: "Actualizar", onClick: () => startProCheckout() } });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = createRecorder(stream);
      mr.onerror((e) => logRecorderError("recorder onerror", e, mr));
      mr.onstop = async (blob) => {
        stream.getTracks().forEach((t) => t.stop());
        if (blob.size <= 800) { toast.error("Grabación demasiado corta."); return; }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", new File([blob], `nota.${extForMime(blob.type)}`, { type: blob.type || "audio/webm" }));
          const tr = await api.post("/ai/voice", fd);
          const text = (tr.data.text || "").trim();
          if (text) setNoteText((prev) => (prev ? prev + " " : "") + text);
          else toast.error("No se escuchó nada.");
        } catch (e) {
          if (e?.response?.status === 403) toast.error("El dictado con ClinPed Voice es del Plan Pro.", { action: { label: "Actualizar", onClick: () => startProCheckout() } });
          else if (e?.response?.status === 429) toast.error(e.response.data?.detail || "Alcanzaste el límite de dictado de este mes.");
          else toast.error("No se pudo transcribir el audio.");
        } finally { setTranscribing(false); }
      };
      mr.start();
      mediaRec.current = mr;
      setRecording(true);
    } catch (e) {
      logRecorderError("startRecording", e, mediaRec.current);
      toast.error(micErrorMessage(e));
    }
  };
  const stopDictation = () => {
    if (mediaRec.current) mediaRec.current.stop();
    setRecording(false);
  };

  const openUrl = (att) => `${API}/attachments/${att.attachment_id}/download?auth=${token}`;

  return (
    <div className="space-y-6">
      {/* Subir archivo / escanear */}
      <div className="bg-white rounded-3xl p-6 border border-stone-100 shadow-[0_8px_30px_rgb(61,64,91,0.04)]">
        <div className="flex items-center gap-2 mb-1">
          <FlaskConical className="text-brand-terracotta" size={20} />
          <h3 className="font-outfit font-bold text-brand-ink">Laboratorios y estudios</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-4">Sube el PDF del laboratorio (o cualquier archivo), escanéalo con la cámara, o escribe/dicta una nota. Máx 15 MB.</p>

        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/50">Tipo</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="att-category" className="rounded-xl mt-1 bg-stone-50 border-transparent"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v} data-testid={`att-cat-${c.v}`}>{c.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/50">Título (opcional)</Label>
            <Input data-testid="att-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej. BH 12/06" className="rounded-xl mt-1 bg-stone-50 border-transparent" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input ref={fileRef} type="file" accept="image/*,application/pdf,.doc,.docx" onChange={(e) => uploadFile(e.target.files?.[0])} className="hidden" data-testid="att-file-input" />
            <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={(e) => uploadFile(e.target.files?.[0])} className="hidden" data-testid="att-camera-input" />
            <button data-testid="att-upload-btn" disabled={uploading} onClick={() => fileRef.current?.click()}
              className="flex items-center justify-center gap-1.5 bg-brand-terracotta hover:bg-brand-terracottaDark text-white font-semibold py-2.5 rounded-xl transition-all hover:scale-[1.01] disabled:opacity-60 text-sm">
              {uploading ? <><Loader2 size={15} className="animate-spin" /> {progress}%</> : <><Upload size={15} /> Subir</>}
            </button>
            <button data-testid="att-scan-btn" disabled={uploading} onClick={() => camRef.current?.click()}
              className="flex items-center justify-center gap-1.5 bg-brand-ink hover:opacity-90 text-white font-semibold py-2.5 rounded-xl transition-all hover:scale-[1.01] disabled:opacity-60 text-sm">
              <Camera size={15} /> Escanear
            </button>
          </div>
        </div>
      </div>

      {/* Nota de laboratorio (escribir / dictar) */}
      <div className="bg-white rounded-3xl p-6 border border-stone-100 shadow-[0_8px_30px_rgb(61,64,91,0.04)]">
        <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
          <div className="flex items-center gap-2">
            <StickyNote className="text-brand-sage" size={20} />
            <h3 className="font-outfit font-bold text-brand-ink">Nota de laboratorio</h3>
          </div>
          <span data-testid="clinped-voice-badge" className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full bg-gradient-to-r from-brand-terracotta to-brand-sageDark text-white">
            <AudioLines size={13} /> ClinPed Voice{!premium && <span className="opacity-90">· Pro</span>}
          </span>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Escribe los resultados o díctalos con <b>ClinPed Voice</b> (el dictado es Plan Pro). Puedes analizar la nota con IA y enviarla a la consulta.</p>
        <Input data-testid="lab-note-title" value={noteTitle} onChange={(e) => setNoteTitle(e.target.value)} placeholder="Título (ej. Biometría hemática)" className="rounded-xl bg-stone-50 border-transparent mb-2" />
        <div className="relative">
          <Textarea data-testid="lab-note-text" value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={4}
            placeholder="Ej. Hb 10.2, Hto 31%, VCM 72, leucocitos 8.4..." className="rounded-xl bg-stone-50 border-transparent focus:bg-white pr-12" />
          {!recording ? (
            <button data-testid="lab-note-mic" onClick={startDictation} disabled={transcribing}
              className="absolute right-2 top-2 p-2 rounded-xl bg-brand-terracotta text-white hover:bg-brand-terracottaDark transition-all disabled:opacity-60" title="Dictar con ClinPed Voice (Plan Pro)">
              {transcribing ? <Loader2 size={16} className="animate-spin" /> : <Mic size={16} />}
            </button>
          ) : (
            <button data-testid="lab-note-mic-stop" onClick={stopDictation}
              className="absolute right-2 top-2 p-2 rounded-xl bg-red-600 text-white animate-pulse" title="Detener">
              <Square size={16} />
            </button>
          )}
        </div>
        <div className="flex justify-end mt-3">
          <button data-testid="lab-note-save" onClick={saveNote} disabled={savingNote || !noteText.trim()}
            className="flex items-center gap-1.5 bg-brand-sage hover:bg-brand-sageDark text-white font-semibold px-4 py-2.5 rounded-xl text-sm transition-all disabled:opacity-60">
            {savingNote ? <Loader2 size={15} className="animate-spin" /> : <StickyNote size={15} />} Guardar nota
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-brand-terracotta" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-3xl p-10 text-center border border-stone-100" data-testid="att-empty">
          <div className="w-14 h-14 rounded-2xl bg-brand-terracotta/10 flex items-center justify-center mx-auto mb-3"><FlaskConical className="text-brand-terracotta" size={26} /></div>
          <p className="font-outfit font-semibold text-brand-ink">Sin laboratorios</p>
          <p className="text-sm text-muted-foreground mt-1">Sube, escanea o escribe el primer resultado de {patient.name}.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="att-list">
          <AnimatePresence>
            {items.map((att) => (
              <motion.div key={att.attachment_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-2xl p-3 border border-stone-100 shadow-[0_8px_30px_rgb(61,64,91,0.04)]" data-testid={`att-card-${att.attachment_id}`}>
                {att.is_note ? (
                  <div className="w-full min-h-[8rem] rounded-xl bg-brand-sage/5 p-3 text-xs text-brand-ink/80 whitespace-pre-wrap overflow-y-auto max-h-40" data-testid={`att-note-${att.attachment_id}`}>
                    {att.note_text}
                  </div>
                ) : (
                  <a href={openUrl(att)} target="_blank" rel="noreferrer" className="block">
                    {att.is_image ? <Thumb att={att} /> : (
                      <div className="w-full h-32 rounded-xl bg-stone-50 flex flex-col items-center justify-center gap-1 text-muted-foreground">
                        <FileText size={30} className="text-brand-sage" />
                        <span className="text-[11px] font-semibold uppercase">{(att.original_filename || "").split(".").pop()}</span>
                      </div>
                    )}
                  </a>
                )}
                <div className="flex items-start justify-between gap-2 mt-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-brand-ink truncate" title={att.title}>{att.title}</p>
                    <p className="text-[11px] text-muted-foreground">{att.is_note ? "Nota" : (CAT_LABEL[att.category] || att.category)} · {new Date(att.created_at).toLocaleDateString("es-MX")}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!att.is_note && (
                      <a href={openUrl(att)} target="_blank" rel="noreferrer" data-testid={`att-open-${att.attachment_id}`}
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-brand-ink hover:bg-muted transition-all"><ExternalLink size={15} /></a>
                    )}
                    <button data-testid={`att-delete-${att.attachment_id}`} onClick={() => remove(att.attachment_id)}
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all"><Trash2 size={15} /></button>
                  </div>
                </div>
                {canAnalyze(att) && (
                  <div className="mt-2.5 flex gap-2">
                    <button data-testid={`att-analyze-${att.attachment_id}`} onClick={() => analyze(att)} disabled={analyzingId === att.attachment_id}
                      className="flex-1 flex items-center justify-center gap-1.5 bg-brand-sage/15 text-brand-sageDark hover:bg-brand-sage/25 text-xs font-bold py-2 rounded-xl transition-all disabled:opacity-60">
                      {analyzingId === att.attachment_id ? <><Loader2 size={14} className="animate-spin" /> Analizando…</> : <><Sparkles size={14} /> Analizar IA</>}
                    </button>
                    {att.is_note && onSendToConsult && (
                      <button data-testid={`att-send-consult-${att.attachment_id}`} onClick={() => onSendToConsult(att.note_text)}
                        className="flex-1 flex items-center justify-center gap-1.5 bg-brand-terracotta/10 text-brand-terracotta hover:bg-brand-terracotta/20 text-xs font-bold py-2 rounded-xl transition-all" title="Anexar a la Exploración física de la consulta">
                        <Send size={14} /> A la consulta
                      </button>
                    )}
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      <Dialog open={!!result} onOpenChange={(o) => !o && setResult(null)}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="att-analysis-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-brand-ink flex items-center gap-2">
              <Sparkles size={18} className="text-brand-sage" /> Análisis IA del estudio
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs font-semibold text-brand-ink/60 -mt-1">{result?.title}</p>
          <div className="text-sm text-brand-ink whitespace-pre-wrap leading-relaxed mt-2" data-testid="att-analysis-text">{result?.text}</div>
          {onSendToConsult && (
            <button data-testid="att-analysis-send-consult" onClick={() => { onSendToConsult(result.text); setResult(null); }}
              className="mt-3 w-full flex items-center justify-center gap-1.5 bg-brand-terracotta text-white hover:bg-brand-terracottaDark text-sm font-semibold py-2.5 rounded-xl transition-all">
              <Send size={15} /> Enviar el análisis a la consulta
            </button>
          )}
          <p className="text-[11px] text-muted-foreground mt-3 border-t border-stone-100 pt-2">Apoyo generado por IA. La interpretación y decisión final corresponden al médico tratante.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
